import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  Output,
  ViewChild,
  OnInit,
} from '@angular/core';
import { optionSelectsModel } from '../../models/option-select.model';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FormatExchangeNamePipe } from '../../../pipes/format-exchange-name.pipe';

@Component({
  selector: 'app-custom-option-select',
  imports: [MatIconModule, CommonModule, FormsModule, TranslateModule, CommonModule, FormatExchangeNamePipe],
  templateUrl: './custom-option-select.component.html',
  styleUrls: ['./custom-option-select.component.scss'],
})
export class CustomOptionSelectComponent implements OnInit {
  constructor(
    private changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) private doc: Document,
    private elementRef: ElementRef,
  ) {}

  @Input() options: optionSelectsModel[] = [];
  @Input() currentValue!: any;
  @Input() disabled: boolean = false;
  @Input() customClasses: string = '';
  @Input() label: string = '';
  @Input() isRequired: boolean = false;
  @Input() noMarginTop = false;

  @ViewChild('selectionDiv') selectionDiv!: any;

  @Output() changedValue: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();
  @Output() optionName: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();

  public openSelection: boolean = false;
  public currentOption: optionSelectsModel = {
    name: '',
    value: 'undefined',
  };
  public focusedOptionIndex: number = -1;

  ngOnInit(): void {
    this.setOption();
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.openSelection = false;
      this.focusedOptionIndex = -1;
    }
  }

  private isInsideCustomOptionSelect(target: any): boolean {
    return this.hasClassOrParentWithClass(target, 'selection-div');
  }

  private hasClassOrParentWithClass(element: any, className: string): boolean {
    while (element) {
      if (element.classList && element.classList.contains(className)) {
        return true;
      }
      element = element.parentNode;
    }
    return false;
  }

  setOption() {
    this.currentOption = this.options.filter((value) => value.value == this.currentValue)[0];
  }

  handleKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.openSelection) {
          this.openSelection = true;
          this.focusedOptionIndex = this.currentOption
            ? this.options.findIndex((opt) => opt.value === this.currentOption.value)
            : 0;
        } else if (this.focusedOptionIndex >= 0) {
          this.selectedOption(this.options[this.focusedOptionIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.openSelection = false;
        this.focusedOptionIndex = -1;
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (!this.openSelection) {
          this.openSelection = true;
          this.focusedOptionIndex = this.currentOption
            ? this.options.findIndex((opt) => opt.value === this.currentOption.value)
            : 0;
        } else {
          this.focusedOptionIndex = Math.min(this.focusedOptionIndex + 1, this.options.length - 1);
          // Skip disabled options
          while (
            this.options[this.focusedOptionIndex]?.value === 'disabled' &&
            this.focusedOptionIndex < this.options.length - 1
          ) {
            this.focusedOptionIndex++;
          }
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.openSelection) {
          this.focusedOptionIndex = Math.max(this.focusedOptionIndex - 1, 0);
          // Skip disabled options
          while (this.options[this.focusedOptionIndex]?.value === 'disabled' && this.focusedOptionIndex > 0) {
            this.focusedOptionIndex--;
          }
        }
        break;

      case 'Tab':
        this.openSelection = false;
        this.focusedOptionIndex = -1;
        break;
    }

    // Update aria-activedescendant
    if (this.openSelection && this.focusedOptionIndex >= 0) {
      const focusedOption = this.options[this.focusedOptionIndex];
      if (focusedOption) {
        this.selectionDiv.nativeElement.setAttribute('aria-activedescendant', `option-${focusedOption.value}`);
      }
    }

    this.changeDetector.detectChanges();
  }

  selectedOption(option: optionSelectsModel, firstTime?: boolean) {
    if (option.value === 'disabled') {
      return;
    }
    this.currentValue = option.value;
    this.currentOption = option;
    this.openSelection = false;
    this.focusedOptionIndex = -1;
    this.optionName.emit(option.name?.toString());
    this.changedValue.emit(option.value?.toString());
    this.changeDetector.detectChanges();
  }
}
